import { Service, computed, signal } from '@angular/core';
import { AuthSession, OnboardingStage, OperatorRole, Principal } from '../models/auth.model';

const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const PRINCIPAL_KEY = 'auth_principal';
const ONBOARDING_STAGE_KEY = 'auth_onboarding_stage';

export type Zone = 'admin' | 'user';

// The /app/** zone is RenderMode.Client only (see app.routes.server.ts), so
// this never runs during SSR — the localStorage guards below are defensive
// regardless.
//
// Token storage: the access token lives in memory only (this signal, never
// persisted) — bounds its exposure to the current page load. The refresh
// token and principal persist in localStorage so a reload or a second tab can
// silently re-authenticate via POST /v1/auth/refresh instead of forcing a
// fresh login. This is short of OWASP's actual recommendation (HttpOnly/
// Secure/SameSite cookies, or a Backend-for-Frontend) specifically for the
// refresh token — neither is available here without the backend issuing
// Set-Cookie instead of a JSON body (see design-handoff/planificacion_fase_2.md
// §Endpoints), which is a contract change outside this frontend's control.
// Worth revisiting with whoever owns that contract if it becomes an option.
@Service()
export class Auth {
  private readonly _accessToken = signal<string | null>(null);
  readonly accessToken = this._accessToken.asReadonly();

  private readonly _refreshToken = signal<string | null>(this.readStored(REFRESH_TOKEN_KEY));
  readonly refreshToken = this._refreshToken.asReadonly();

  private readonly _principal = signal<Principal | null>(this.readPrincipal());
  readonly principal = this._principal.asReadonly();

  private readonly _onboardingStage = signal<OnboardingStage | null>(this.readStage());
  readonly onboardingStage = this._onboardingStage.asReadonly();

  readonly isAuthenticated = computed(() => this._accessToken() !== null);

  readonly email = computed(() => this._principal()?.email ?? null);

  // delegate (the obra social's invited sub-account) is always 'user'.
  // backoffice_operator splits by role: admin_os (the obra social's own
  // admin — an OperatorRole value, not a ProfileType, per fase2 §Principal y
  // roles) is 'user' too; general_admin/reviewer (internal IEMSA staff) are
  // 'admin'. Anything else (or a missing principal) has no zone — including a
  // backoffice_operator whose role isn't one of those three, e.g. the
  // 'unassigned' the backend sets on an admin_os replaced via
  // designar_admin_os. The backend can send roles outside the OperatorRole
  // union, so this whitelists known roles instead of defaulting to 'admin'.
  readonly zone = computed<Zone | null>(() => {
    const principal = this._principal();
    if (!principal) {
      return null;
    }
    if (principal.profileType === 'delegate') {
      return 'user';
    }
    switch (principal.role) {
      case 'admin_os':
        return 'user';
      case 'general_admin':
      case 'reviewer':
        return 'admin';
      default:
        return null;
    }
  });

  readonly operatorRole = computed<OperatorRole | null>(() => this._principal()?.role ?? null);
  readonly obraSocialId = computed(() => this._principal()?.obraSocialId ?? null);
  // Lives on Principal (see its delegateId comment) rather than as its own
  // persisted signal — it's already covered by principal's own
  // localStorage persistence, no separate storage needed.
  readonly delegadoId = computed(() => this._principal()?.delegateId ?? null);
  readonly isGeneralAdmin = computed(() => this.operatorRole() === 'general_admin');
  readonly isAdminOs = computed(() => this.operatorRole() === 'admin_os');
  readonly isDelegate = computed(() => this._principal()?.profileType === 'delegate');
  readonly passwordChangeRecommended = computed(() => this._principal()?.passwordChangeRecommended ?? false);

  applySession(session: AuthSession): void {
    this._accessToken.set(session.accessToken);
    this._refreshToken.set(session.refreshToken);
    this._principal.set(session.principal);
    this._onboardingStage.set(session.onboardingStage);
    this.writeStored(REFRESH_TOKEN_KEY, session.refreshToken);
    this.writeStored(PRINCIPAL_KEY, JSON.stringify(session.principal));
    this.writeOnboardingStage(session.onboardingStage);
  }

  // No backend endpoint for this yet — advances the client-mocked stage
  // directly, same stopgap as UsersStore's transition methods but scoped to
  // the signed-in identity instead of an admin-managed entity.
  setOnboardingStage(stage: OnboardingStage): void {
    this._onboardingStage.set(stage);
    this.writeOnboardingStage(stage);
  }

  clear(): void {
    this._accessToken.set(null);
    this._refreshToken.set(null);
    this._principal.set(null);
    this._onboardingStage.set(null);
    this.removeStored(REFRESH_TOKEN_KEY);
    this.removeStored(PRINCIPAL_KEY);
    this.removeStored(ONBOARDING_STAGE_KEY);
  }

  private readPrincipal(): Principal | null {
    const raw = this.readStored(PRINCIPAL_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as Principal;
    } catch {
      return null;
    }
  }

  private readStage(): OnboardingStage | null {
    return (this.readStored(ONBOARDING_STAGE_KEY) as OnboardingStage | null) ?? null;
  }

  private writeOnboardingStage(stage: OnboardingStage | null): void {
    if (stage === null) {
      this.removeStored(ONBOARDING_STAGE_KEY);
    } else {
      this.writeStored(ONBOARDING_STAGE_KEY, stage);
    }
  }

  private readStored(key: string): string | null {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  }

  private writeStored(key: string, value: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  private removeStored(key: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }
}
